import styled, { createGlobalStyle } from "styled-components";
import { Link } from 'react-router-dom';
import { OverridableComponent } from "@mui/material/OverridableComponent";
import {
  Button,
  ButtonTypeMap,
  Container,
  TableCell,
  TableRow,
  Typography,
  TypographyTypeMap,
  tableCellClasses,
} from "@mui/material";
import LoginBackground from "../assets/background.jpg";
import BarlowRegular from "../assets/fonts/Barlow-Regular.ttf";
import BarlowMedium from "../assets/fonts/Barlow-Medium.ttf";
import BarlowSemiBold from "../assets/fonts/Barlow-SemiBold.ttf";
import BarlowBold from "../assets/fonts/Barlow-Bold.ttf";
import IBMPlexMonoRegular from "../assets/fonts/IBMPlexMono-Regular.ttf";
import IBMPlexMonoMedium from "../assets/fonts/IBMPlexMono-Medium.ttf";
import IBMPlexMonoSemiBold from "../assets/fonts/IBMPlexMono-SemiBold.ttf";
import "../assets/fonts/OFL-1.1.txt";
import "../assets/fonts/THIRD_PARTY_NOTICES.md";

export const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: "Barlow";
    src: url(${BarlowRegular}) format("truetype");
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }

  @font-face {
    font-family: "Barlow";
    src: url(${BarlowMedium}) format("truetype");
    font-style: normal;
    font-weight: 500;
    font-display: swap;
  }

  @font-face {
    font-family: "Barlow";
    src: url(${BarlowSemiBold}) format("truetype");
    font-style: normal;
    font-weight: 600;
    font-display: swap;
  }

  @font-face {
    font-family: "Barlow";
    src: url(${BarlowBold}) format("truetype");
    font-style: normal;
    font-weight: 700;
    font-display: swap;
  }

  @font-face {
    font-family: "IBM Plex Mono";
    src: url(${IBMPlexMonoRegular}) format("truetype");
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }

  @font-face {
    font-family: "IBM Plex Mono";
    src: url(${IBMPlexMonoMedium}) format("truetype");
    font-style: normal;
    font-weight: 500;
    font-display: swap;
  }

  @font-face {
    font-family: "IBM Plex Mono";
    src: url(${IBMPlexMonoSemiBold}) format("truetype");
    font-style: normal;
    font-weight: 600;
    font-display: swap;
  }

  :root {
    --bg: #0A101B;
    --panel: #101827;
    --panel2: #16202F;
    --panel3: #1C2939;
    --line: #1F2C40;
    --line2: #2A3A52;
    --text: #E9EEF6;
    --muted: #8B97AB;
    --faint: #5A6779;
    --green: #34C77B;
    --green-bg: rgba(52, 199, 123, 0.12);
    --yellow: #F5D000;
    --yellow-bg: rgba(245, 208, 0, 0.16);
    --amber: #FFB224;
    --amber-bg: rgba(255, 178, 36, 0.12);
    --red: #FF5C57;
    --red-bg: rgba(255, 92, 87, 0.13);
    --blue: #6AA6FF;
    --blue-bg: rgba(106, 166, 255, 0.12);
    --violet: #B48CFF;
    --shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
    --font-ui: "Barlow", "Helvetica Neue", Arial, sans-serif;
    --font-mono: "IBM Plex Mono", Menlo, Monaco, Consolas, monospace;
    color-scheme: dark;
  }

  body.light {
    --bg: #EDF0F5;
    --panel: #FFFFFF;
    --panel2: #F5F7FB;
    --panel3: #EBEFF6;
    --line: #E1E6EE;
    --line2: #CDD5E1;
    --text: #141D2B;
    --muted: #5C6A7E;
    --faint: #93A0B2;
    --green: #159A5B;
    --green-bg: rgba(21, 154, 91, 0.10);
    --yellow: #C9A000;
    --yellow-bg: rgba(201, 160, 0, 0.14);
    --amber: #B26A00;
    --amber-bg: rgba(224, 140, 20, 0.13);
    --red: #D6362F;
    --red-bg: rgba(214, 54, 47, 0.09);
    --blue: #2E6BDF;
    --blue-bg: rgba(46, 107, 223, 0.09);
    --shadow: 0 8px 24px rgba(20, 35, 60, 0.10);
    color-scheme: light;
  }

 * {
    margin: 0;
    padding: 0;
    outline: 0;
    box-sizing: border-box;
  }

  *:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 2px;
  }

  ::selection {
    background: var(--blue-bg);
    color: var(--text);
  }

  a {
    color: inherit;
  }

  img {
    max-width: 100%;
  }

  iframe {
    border: 0;
  }

  .marker-label {
    margin-left: calc(50% - 7px);
    margin-bottom: 6px;
    text-shadow: -1px 1px 1px #ffffff;
    font-weight: 500;
  }

  .label-purple {
    color: #560063 !important;
  }

  .label-cyan {
    color: #007b82 !important;
  }

  .label-pink {
    color: #ff278d !important;
  }

  body {
    min-width: 320px;
    min-height: 100vh;
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-ui);
    transition: background-color 160ms ease, color 160ms ease;
  }

  #root {
    min-height: 100vh;
  }

  .app-route-layout,
  .app-route-layout > * {
    min-width: 0;
  }

  .MuiTableContainer-root {
    scrollbar-color: var(--line2) transparent;
  }

  .MuiDialog-paper,
  .MuiDrawer-paper,
  .MuiPopover-paper,
  .MuiMenu-paper {
    color: var(--text);
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  .mono,
  [data-operational-value="true"] {
    font-family: var(--font-mono) !important;
    font-variant-numeric: tabular-nums;
  }

  div[name="warning-box"] {
    position: absolute;
    bottom: 0;
  }

  .jsoneditor {
    border: 0;
  }

  .settings-json-editor-root {
    height: 100%;
    min-height: 0;
    width: 100%;
  }

  .settings-json-editor-root > .jsoneditor {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .settings-json-editor-root .jsoneditor-outer {
    flex: 1 1 auto;
    min-height: 0;
    background: var(--panel);
  }

  .settings-json-editor-root .jsoneditor,
  .settings-json-editor-root .ace_editor,
  .settings-json-editor-root .ace_gutter,
  .settings-json-editor-root textarea.jsoneditor-text {
    background: var(--panel);
    border-color: var(--line);
  }

  .settings-json-editor-root textarea.jsoneditor-text {
    color: var(--text);
    caret-color: var(--text);
  }

  .settings-json-editor-root .ace_editor,
  .settings-json-editor-root .ace_editor *,
  .settings-json-editor-root .ace_text-layer,
  .settings-json-editor-root .ace_line,
  .settings-json-editor-root .ace_cursor {
    font-family: var(--font-mono) !important;
    font-variant-ligatures: none !important;
  }

  .jsoneditor-menu {
    display: none;
  }

  div.jsoneditor-outer.has-main-menu-bar {
    padding-top: 0;
    margin-top: 0;
  }

  .zammad-form-modal {
    .zammad-form-modal-backdrop {
      background: rgba(4, 8, 15, 0.72);
    }

    .zammad-form-modal-body {
      border: 1px solid var(--line2);
      border-radius: 12px;
      width: calc(100% - 32px);
      max-width: 536px;
      padding: 32px;
      background: var(--panel);
      color: var(--text);
      box-shadow: var(--shadow);
    }

    h2 {
      margin: 0;
      font-family: var(--font-ui);
      font-weight: 700;
      font-size: 1.75rem;
      line-height: 1.2;
      letter-spacing: normal;
      padding-bottom: 28px;
      color: var(--text);
    }

    .btn {
      font-family: var(--font-ui);
      font-weight: 600;
      font-size: 0.9375rem;
      line-height: 1.4;
      letter-spacing: normal;
      min-width: 64px;
      padding: 8px 22px;
      border-radius: 9px;
      border: none;
      background: var(--blue);
      color: #fff;
      width: 200px;
      min-height: 48px;
      display: block;
      margin: 24px auto 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      cursor: pointer;
    }

    .zammad-form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin: 24px 0 8px;
    }

    .zammad-form-actions .btn {
      width: auto;
      min-width: 96px;
      margin: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .zammad-form-cancel {
      background: transparent;
      color: var(--text);
      border: 1px solid var(--line2);
      box-shadow: none;
    }

    .btn:hover {
      filter: brightness(1.08);
    }

    .btn:focus-visible {
      outline: 2px solid var(--blue);
      outline-offset: 3px;
    }

    .zammad-form-control {
      font-family: var(--font-ui);
      font-size: 16px;
      color: var(--text);
      background: var(--panel2);
      height: 56px;
      margin: 0;
      display: block;
      min-width: 0;
      width: 100%;
      padding: 16.5px 14px;
      border: 1px solid var(--line2);
      border-radius: 9px;
    }

    .zammad-form-control:focus {
      border-color: var(--blue);
      box-shadow: 0 0 0 2px var(--blue-bg);
      outline: none;
    }

    .zammad-form-control::placeholder {
      color: var(--faint);
    }

    textarea.zammad-form-control {
      height: auto;
    }

    .zammad-form-group {
      position: relative;
      margin-bottom: 16px;

      label {
        position: absolute;
        padding: 0 6px;
        top: -7px;
        left: 9px;
        background: var(--panel);
        font-size: 0.75em;
        color: var(--muted);
      }
    }

    .zammad-form-thankyou {
      font-family: var(--font-ui);
      font-size: 16px;
      color: var(--text);
    }

    @media (max-width: 600px) {
      .zammad-form-modal-body {
        padding: 24px 20px;
      }

      h2 {
        font-size: 1.5rem;
        padding-bottom: 22px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      scroll-behavior: auto !important;
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media print {
    :root,
    body,
    body.light {
      --bg: #ffffff;
      --panel: #ffffff;
      --panel2: #f5f5f5;
      --panel3: #eeeeee;
      --line: #d7d7d7;
      --line2: #bdbdbd;
      --text: #111111;
      --muted: #3d3d3d;
      --faint: #626262;
      color-scheme: light;
      background: #ffffff !important;
      color: #111111 !important;
    }

    .app-shell-navigation,
    [data-print-hide="true"] {
      display: none !important;
    }

    .app-shell-content,
    .app-route-layout {
      width: 100% !important;
      max-width: none !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      color: #111111 !important;
    }

    .MuiPaper-root,
    .MuiCard-root,
    .MuiTableCell-root {
      color: #111111 !important;
      background: #ffffff !important;
      box-shadow: none !important;
    }
  }
`;

export const Page = styled.div`
  padding: 32px 0 48px;

  @media (max-width: 600px) {
    padding: 20px 0;
  }
`;

export const FiltersWrapper = styled.div`
  padding: 20px 0;

  @media (max-width: 600px) {
    padding: 0;
  }
`;

export const Heading: OverridableComponent<TypographyTypeMap> = styled(
  Typography
)`
  padding-bottom: 24px;
  color: var(--text);
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    padding-bottom: 10px;
    font-size: 1.6rem !important;
  }
`;

export const CusotmIconButton: OverridableComponent<ButtonTypeMap> = styled(Button)`
  margin-right: 10px !important;

  @media (max-width: 600px) {
    padding: 7px 13px 7px 21px !important;
  }
`;

export const Cell: OverridableComponent<any> = styled(TableCell)`
  padding: 10px !important;
`;

export const HeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--panel3)",
    color: "var(--text)",
    padding: "10px !important",
    borderBottom: "1px solid var(--line)",
    cursor: "default",
  },
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: "var(--panel3)",
    color: "var(--text)",
    padding: "10px !important",
    borderBottom: "1px solid var(--line)",
  },
}));

export const GrayRow: OverridableComponent<any> = styled(TableRow)`
  background-color: var(--panel2);
`;

export const ButtonText = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
`;

export const StyledContainer: OverridableComponent<any> = styled(Container)`
  background-image: url(${LoginBackground});
  background-position: center;
  background-size: cover;
  max-width: 100%;
  height: calc(100vh - var(--app-nav-offset, 64px));
  overflow: auto;
  display: flex !important;
  justify-content: center;
  align-items: center;
  position: relative;

  ::before {
    /* Create the pseudo-element to hold the pattern */
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-image: radial-gradient(#000000 0.6px, #22222240 0.6px),
      radial-gradient(#000000 0.6px, #22222240 0.6px);
    background-size: 8px 8px;
    background-position: 0 0, 4px 4px;
  }
`;

export const Clear = styled.div`
  width: 27px;
  height: 27px;
  position: absolute;
  top: 15px;
  right: 40px;
  padding: 4px;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  opacity: 0;

  :hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

export const DatePickerWrapper = styled.div`
  position: relative;

  :hover > ${Clear} {
    opacity: 1;
  }
`;

export const WhiteBackground = styled.div`
  background-color: var(--bg);
  color: var(--text);
  min-height: calc(100vh - var(--app-nav-offset, 64px));
  height: calc(100% - var(--app-nav-offset, 64px));
`;

export const InlineLink: OverridableComponent<any> = styled(Link)`
  display: inline-block;
`;
